if (!customElements.get('wacc-header')) {
  class WaccHeader extends HTMLElement {
    connectedCallback() {
      this.mobileMenu = this.querySelector('#wacc-mobile-menu');
      this.openMobileButton = this.querySelector('[data-wacc-menu-open]');
      this.closeMobileButton = this.querySelector('[data-wacc-menu-close]');
      this.mobileShopToggle = this.querySelector('[data-wacc-mobile-shop-toggle]');
      this.mobileShopPanel = this.querySelector('[data-wacc-mobile-shop-panel]');
      this.shopRoot = this.querySelector('[data-wacc-shop]');
      this.shopTrigger = this.querySelector('[data-wacc-shop-trigger]');
      this.shopMenu = this.querySelector('[data-wacc-shop-menu]');
      this.megaLinks = Array.from(this.querySelectorAll('[data-wacc-mega-link]'));
      this.previewPanels = Array.from(this.querySelectorAll('[data-wacc-preview-panel]'));
      this.searchToggle = this.querySelector('[data-wacc-search-toggle]');
      this.searchPanel = this.querySelector('[data-wacc-search-panel]');
      this.searchInput = this.querySelector('[data-wacc-search-input]');
      this.searchClose = this.querySelector('[data-wacc-search-close]');
      this.closeTimer = null;
      /* CC-547 Business Coffee menu: own elements and own timer, so the Shop menu code is untouched. */
      this.bizRoot = this.querySelector('[data-wacc-biz]');
      this.bizTrigger = this.querySelector('[data-wacc-biz-trigger]');
      this.bizMenu = this.querySelector('[data-wacc-biz-menu]');
      this.bizLinks = Array.from(this.querySelectorAll('[data-wacc-biz-link]'));
      this.bizPanels = Array.from(this.querySelectorAll('[data-wacc-biz-panel]'));
      this.mobileBizToggle = this.querySelector('[data-wacc-mobile-biz-toggle]');
      this.mobileBizPanel = this.querySelector('[data-wacc-mobile-biz-panel]');
      this.bizCloseTimer = null;
      this.bizOpenAtPointerDown = false;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleShopFocusOut = this.handleShopFocusOut.bind(this);
      this.handleShopKeydown = this.handleShopKeydown.bind(this);
      this.handleMouseEnter = this.openShopMenu.bind(this);
      this.handleMouseLeave = this.scheduleShopClose.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.handleSearchOutsideClick = this.handleSearchOutsideClick.bind(this);

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      document.addEventListener('keydown', this.handleDocumentKeydown);
      document.addEventListener('click', this.handleSearchOutsideClick);
      this.handleScroll();

      this.openMobileButton?.addEventListener('click', () => this.openMobileMenu());
      this.closeMobileButton?.addEventListener('click', () => this.closeMobileMenu());
      this.mobileShopToggle?.addEventListener('click', () => this.toggleMobileShop());
      this.searchToggle?.addEventListener('click', () => this.toggleSearch());
      this.searchClose?.addEventListener('click', () => this.closeSearch());

      this.shopRoot?.addEventListener('mouseenter', this.handleMouseEnter);
      this.shopRoot?.addEventListener('mouseleave', this.handleMouseLeave);
      this.shopRoot?.addEventListener('focusin', () => this.openShopMenu());
      this.shopRoot?.addEventListener('focusout', this.handleShopFocusOut);
      this.shopRoot?.addEventListener('keydown', this.handleShopKeydown);
      this.shopTrigger?.addEventListener('click', () => this.toggleShopMenu());

      this.megaLinks.forEach((link, index) => {
        link.addEventListener('mouseenter', () => this.activatePreview(index));
        link.addEventListener('focus', () => this.activatePreview(index));
      });

      this.bindBusinessMenu();

      if (window.subscribe && typeof PUB_SUB_EVENTS !== 'undefined') { /* WA-010b */
        this.unsubscribeCart = window.subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      document.removeEventListener('click', this.handleSearchOutsideClick);
      this.shopRoot?.removeEventListener('mouseenter', this.handleMouseEnter);
      this.shopRoot?.removeEventListener('mouseleave', this.handleMouseLeave);
      this.shopRoot?.removeEventListener('focusout', this.handleShopFocusOut);
      this.shopRoot?.removeEventListener('keydown', this.handleShopKeydown);
      if (typeof this.unsubscribeCart === 'function') this.unsubscribeCart();
    }

    handleScroll() {
      this.classList.toggle('is-stuck', window.scrollY > 8);
    }

    openMobileMenu() {
      if (!this.mobileMenu) return;
      this.mobileMenu.classList.add('is-open');
      this.mobileMenu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.closeMobileButton?.focus();
    }

    closeMobileMenu() {
      if (!this.mobileMenu) return;
      this.mobileMenu.classList.remove('is-open');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.openMobileButton?.focus();
    }

    toggleMobileShop() {
      if (!this.mobileShopToggle || !this.mobileShopPanel) return;
      const isOpen = this.mobileShopToggle.getAttribute('aria-expanded') === 'true';
      this.mobileShopToggle.setAttribute('aria-expanded', String(!isOpen));
      this.mobileShopPanel.classList.toggle('is-open', !isOpen);
    }

    openShopMenu() {
      window.clearTimeout(this.closeTimer);
      this.shopMenu?.classList.add('is-open');
      this.shopTrigger?.setAttribute('aria-expanded', 'true');
    }

    closeShopMenu() {
      this.shopMenu?.classList.remove('is-open');
      this.shopTrigger?.setAttribute('aria-expanded', 'false');
      this.activatePreview(0);
    }

    scheduleShopClose() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.closeShopMenu(), 120);
    }

    toggleShopMenu() {
      const isOpen = this.shopTrigger?.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        this.closeShopMenu();
      } else {
        this.openShopMenu();
        this.megaLinks[0]?.focus();
      }
    }

    activatePreview(index) {
      this.megaLinks.forEach((link, linkIndex) => {
        const isActive = linkIndex === index;
        link.classList.toggle('is-active', isActive);
        link.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.previewPanels.forEach((panel, panelIndex) => {
        panel.classList.toggle('is-active', panelIndex === index);
      });
    }

    handleShopFocusOut(event) {
      if (event.relatedTarget && this.shopRoot?.contains(event.relatedTarget)) return;
      this.closeShopMenu();
    }

    handleShopKeydown(event) {
      const activeIndex = this.megaLinks.findIndex((link) => link === document.activeElement);

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        const nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % this.megaLinks.length;
        this.activatePreview(nextIndex);
        this.megaLinks[nextIndex]?.focus();
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const previousIndex = activeIndex <= 0 ? this.megaLinks.length - 1 : activeIndex - 1;
        this.activatePreview(previousIndex);
        this.megaLinks[previousIndex]?.focus();
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.activatePreview(0);
        this.megaLinks[0]?.focus();
      }

      if (event.key === 'End') {
        event.preventDefault();
        const lastIndex = this.megaLinks.length - 1;
        this.activatePreview(lastIndex);
        this.megaLinks[lastIndex]?.focus();
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeShopMenu();
        this.shopTrigger?.focus();
      }
    }

    /* ---- CC-547 Business Coffee mega menu ---- */
    bindBusinessMenu() {
      if (this.bizRoot && this.bizTrigger && this.bizMenu) {
        this.bizRoot.addEventListener('mouseenter', () => this.openBizMenu());
        this.bizRoot.addEventListener('mouseleave', () => this.scheduleBizClose());
        this.bizRoot.addEventListener('focusin', () => this.openBizMenu());
        this.bizRoot.addEventListener('focusout', (event) => {
          if (event.relatedTarget && this.bizRoot.contains(event.relatedTarget)) return;
          this.closeBizMenu();
        });
        this.bizRoot.addEventListener('keydown', (event) => this.handleBizKeydown(event));

        /* The trigger is a real link to the hub. A mouse click or Enter follows it. A first tap on a
           touch screen opens the menu instead (there is no hover); a second tap follows the link. */
        this.bizTrigger.addEventListener('pointerdown', (event) => {
          this.bizOpenAtPointerDown = this.bizMenu.classList.contains('is-open');
          this.bizPointerType = event.pointerType;
        });
        this.bizTrigger.addEventListener('click', (event) => {
          const viaTouch = this.bizPointerType === 'touch' || this.bizPointerType === 'pen';
          this.bizPointerType = null;
          if (viaTouch && !this.bizOpenAtPointerDown) {
            event.preventDefault();
            this.openBizMenu();
          }
        });

        this.bizLinks.forEach((link, index) => {
          link.addEventListener('mouseenter', () => this.activateBizPreview(index));
          link.addEventListener('focus', () => this.activateBizPreview(index));
        });

        /* Only one mega menu at a time. */
        this.shopRoot?.addEventListener('mouseenter', () => this.closeBizMenu());
        this.shopRoot?.addEventListener('focusin', () => this.closeBizMenu());
        document.addEventListener('click', (event) => {
          if (this.bizMenu.classList.contains('is-open') && !this.bizRoot.contains(event.target)) this.closeBizMenu();
        });
      }

      this.mobileBizToggle?.addEventListener('click', () => {
        if (!this.mobileBizPanel) return;
        const isOpen = this.mobileBizToggle.getAttribute('aria-expanded') === 'true';
        this.mobileBizToggle.setAttribute('aria-expanded', String(!isOpen));
        this.mobileBizPanel.classList.toggle('is-open', !isOpen);
      });

      /* GA4: nav_click with the menu name and the item slug. No personal data. */
      this.addEventListener('click', (event) => {
        const item = event.target.closest('[data-wacc-nav-item]');
        if (!item || !this.contains(item)) return;
        const payload = { menu: 'business_coffee', item: item.getAttribute('data-wacc-nav-item') };
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'nav_click', payload);
        } else {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(Object.assign({ event: 'nav_click' }, payload));
        }
      });
    }

    openBizMenu() {
      window.clearTimeout(this.bizCloseTimer);
      if (this.shopMenu?.classList.contains('is-open')) this.closeShopMenu();
      this.bizMenu?.classList.add('is-open');
      this.bizTrigger?.setAttribute('aria-expanded', 'true');
    }

    closeBizMenu() {
      window.clearTimeout(this.bizCloseTimer);
      if (!this.bizMenu?.classList.contains('is-open')) return;
      this.bizMenu.classList.remove('is-open');
      this.bizTrigger?.setAttribute('aria-expanded', 'false');
      this.activateBizPreview(0);
    }

    scheduleBizClose() {
      window.clearTimeout(this.bizCloseTimer);
      this.bizCloseTimer = window.setTimeout(() => this.closeBizMenu(), 120);
    }

    activateBizPreview(index) {
      this.bizLinks.forEach((link, linkIndex) => link.classList.toggle('is-active', linkIndex === index));
      this.bizPanels.forEach((panel, panelIndex) => panel.classList.toggle('is-active', panelIndex === index));
    }

    handleBizKeydown(event) {
      const count = this.bizLinks.length;
      if (!count) return;
      const activeIndex = this.bizLinks.findIndex((link) => link === document.activeElement);
      const focusLink = (index) => {
        event.preventDefault();
        this.openBizMenu();
        this.activateBizPreview(index);
        this.bizLinks[index]?.focus();
      };

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        focusLink(activeIndex < 0 ? 0 : (activeIndex + 1) % count);
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        focusLink(activeIndex <= 0 ? count - 1 : activeIndex - 1);
      } else if (event.key === 'Home') {
        focusLink(0);
      } else if (event.key === 'End') {
        focusLink(count - 1);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeBizMenu();
        this.bizTrigger?.focus();
        /* Focus landing back on the trigger would reopen the menu through focusin. */
        this.closeBizMenu();
      }
    }

    handleDocumentKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.searchPanel && !this.searchPanel.hidden) {
        this.closeSearch();
        return;
      }
      if (this.mobileMenu?.classList.contains('is-open')) {
        this.closeMobileMenu();
        return;
      }

      if (this.bizMenu?.classList.contains('is-open')) {
        this.closeBizMenu();
        this.bizTrigger?.focus();
        return;
      }

      if (this.shopMenu?.classList.contains('is-open')) {
        this.closeShopMenu();
        this.shopTrigger?.focus();
      }
    }

    openSearch() {
      if (!this.searchPanel) return;
      this.searchPanel.hidden = false;
      this.searchToggle?.setAttribute('aria-expanded', 'true');
      // Focus on next frame so the panel is in the DOM and visible
      requestAnimationFrame(() => this.searchInput?.focus());
    }

    closeSearch() {
      if (!this.searchPanel) return;
      this.searchPanel.hidden = true;
      this.searchToggle?.setAttribute('aria-expanded', 'false');
      this.searchToggle?.focus();
    }

    toggleSearch() {
      if (!this.searchPanel) return;
      if (this.searchPanel.hidden) this.openSearch();
      else this.closeSearch();
    }

    handleSearchOutsideClick(event) {
      if (!this.searchPanel || this.searchPanel.hidden) return;
      // Click inside the header (including the panel itself or the toggle) -> ignore
      if (this.contains(event.target)) return;
      this.closeSearch();
    }

    handleCartUpdate(event) {
      const count = event?.cartData?.item_count;
      if (typeof count !== 'number') return;
      const countElement = this.querySelector('[data-wacc-cart-count]');
      if (!countElement) return;
      countElement.textContent = count;
      countElement.toggleAttribute('hidden', count === 0);
    }
  }

  customElements.define('wacc-header', WaccHeader);
}
